import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { getDatabases, getLifecycleEvents, revertLifecycleRun } from "../api/adminApi";
import { Hero, Dropdown, SegControl, EmptyState } from "../components/adminSurface/AdminSurface";
import { RecycleArt } from "../components/adminSurface/heroArt";

const PAGE_SIZE = 100;
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_ROWS = 100000;

function dbId(d) { return d?.id ?? d?.Id ?? d?.dbInfoId ?? d?.DBInfoId; }
function dbNameOf(d) { return d?.name ?? d?.Name ?? d?.displayName ?? d?.DisplayName ?? d?.dbName ?? d?.DbName ?? String(dbId(d)); }

function toDateStr(d) { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; }
function fmtAbs(raw) {
  if (!raw) return "";
  try { const d = new Date(raw); if (isNaN(d)) return String(raw); const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  } catch { return String(raw); }
}
function fmtRel(raw) {
  if (!raw) return "";
  try { const diff = Date.now() - new Date(raw).getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 7 * 86400000) return `${Math.floor(diff / 86400000)}d ago`;
    return fmtAbs(raw).slice(0, 10);
  } catch { return fmtAbs(raw); }
}
function initials(name) {
  if (!name) return "•";
  const parts = name.trim().split(/[\s@.]+/);
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}
const AVATAR_COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#6366f1"];
function avatarColor(name) {
  if (!name) return "#9ca3af";
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function formatBytes(n) {
  if (!n || n <= 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

// Colour by lifecycle action (mirrors AuditLogs' action palette).
const STRATEGY_META = {
  Move:          { color: "#0e7490", bg: "#cffafe" },
  SoftDelete:    { color: "#b45309", bg: "#fef3c7" },
  RowDelete:     { color: "#b91c1c", bg: "#fee2e2" },
  PartitionDrop: { color: "#b91c1c", bg: "#fee2e2" },
};
function strategyMeta(s) { return STRATEGY_META[s] || { color: "#374151", bg: "#f3f4f6" }; }

const STRATEGIES = ["Move", "SoftDelete", "RowDelete", "PartitionDrop"];
const STATUSES = ["Success", "Partial", "Failed", "Blocked"];

const RANGES = [
  { label: "1h", getFrom: () => toDateStr(new Date(Date.now() - 3600000)) },
  { label: "24h", getFrom: () => toDateStr(new Date(Date.now() - 86400000)) },
  { label: "7d", getFrom: () => toDateStr(new Date(Date.now() - 7 * 86400000)) },
  { label: "30d", getFrom: () => toDateStr(new Date(Date.now() - 30 * 86400000)) },
  { label: "All", getFrom: () => "" },
];


export default function LifecycleEventsPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [databases, setDatabases] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [reverting, setReverting] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // filter inputs
  const [search, setSearch] = useState("");
  const [dbFilter, setDbFilter] = useState("");
  const [strategy, setStrategy] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState(() => toDateStr(new Date(Date.now() - 30 * 86400000)));
  const [toDate, setToDate] = useState("");
  const [activeRange, setActiveRange] = useState("30d");
  const [applied, setApplied] = useState(() => ({ fromUtc: toDateStr(new Date(Date.now() - 30 * 86400000)) }));

  useEffect(() => {
    if (!token) return;
    getDatabases(token).then((d) => setDatabases(Array.isArray(d) ? d : (d?.databases ?? d?.Databases ?? []))).catch(() => {});
  }, [token]);

  const queryFor = useCallback((f) => ({
    dbId: f.dbId || undefined, strategy: f.strategy || undefined, status: f.status || undefined,
    search: f.search || undefined, fromUtc: f.fromUtc || undefined, toUtc: f.toUtc || undefined,
  }), []);

  const load = useCallback(async (pageIndex, f) => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const res = await getLifecycleEvents(token, { ...queryFor(f), page: pageIndex + 1, pageSize: PAGE_SIZE });
      setItems(Array.isArray(res?.events) ? res.events : []);
      setTotal(res?.total ?? 0);
    } catch (e) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  }, [token, queryFor]);

  useEffect(() => { load(page, applied); }, [page, applied, load]);

  const applyFilters = () => { setPage(0); setActiveRange(""); setApplied({ search, dbId: dbFilter, strategy, status, fromUtc: fromDate, toUtc: toDate }); };
  const applyRange = (r) => { const from = r.getFrom(); setFromDate(from); setToDate(""); setActiveRange(r.label); setPage(0); setApplied({ search, dbId: dbFilter, strategy, status, fromUtc: from, toUtc: "" }); };
  const resetFilters = () => { setSearch(""); setDbFilter(""); setStrategy(""); setStatus(""); setFromDate(""); setToDate(""); setActiveRange("All"); setPage(0); setApplied({}); };
  const refresh = () => load(page, applied);

  async function doRevert(ev) {
    if (!window.confirm(t("lifecycleEvents.revertConfirm"))) return;
    setError(""); setInfo(""); setReverting(ev.id);
    try {
      const r = await revertLifecycleRun(token, ev.id);
      setInfo(t("lifecycleEvents.revertDone", { docs: r?.docsMoved ?? r?.DocsMoved ?? 0 }));
      await load(page, applied);
    } catch (e) { setError(e?.message || String(e)); }
    finally { setReverting(null); }
  }

  async function handleExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const all = []; let p = 1;
      while (true) {
        const res = await getLifecycleEvents(token, { ...queryFor(applied), page: p, pageSize: EXPORT_PAGE_SIZE });
        const batch = res?.events ?? [];
        all.push(...batch);
        if (batch.length < EXPORT_PAGE_SIZE || all.length >= (res?.total ?? all.length) || all.length >= EXPORT_MAX_ROWS) break;
        p++;
      }
      if (all.length === 0) { alert(t("lifecycleEvents.empty")); return; }
      const esc = (v) => { const s = String(v ?? ""); return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
      const header = ["When", "User", "Action", "Status", "Docs", "Bytes", "Database", "Destination"];
      const rows = all.map((e) => [fmtAbs(e.runAtUtc), e.requestedBy ?? "", e.strategy ?? "", e.status ?? "",
        e.docsActioned ?? 0, e.bytesReclaimed ?? 0, e.dbName ?? "", destLabel(e)]);
      const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a"); a.href = url; a.download = `lifecycle-events-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert("Export failed: " + (e?.message || e)); }
    finally { setExporting(false); }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const strategyLabel = (s) =>
    s === "Move" ? t("lifecycle.actionMove") : s === "SoftDelete" ? t("lifecycle.actionSoftDelete")
      : s === "PartitionDrop" ? t("lifecycleEvents.strategyPartitionDrop") : t("lifecycle.actionDelete");

  const revertCell = (ev) => {
    if (ev.revertable) return (
      <button type="button" className="link-btn" disabled={reverting !== null}
              onClick={(e) => { e.stopPropagation(); doRevert(ev); }}>
        {reverting === ev.id ? t("lifecycleEvents.reverting") : t("lifecycleEvents.revertBtn")}
      </button>
    );
    const key = { isRevert: "revIsRevert", softDeleteSoon: "revSoftSoon", physicalIrreversible: "revPhysical",
      notSuccess: "revNotSuccess", alreadyReverted: "revAlready", crossDbSoon: "revCrossDb", legacyNoSource: "revLegacy" }[ev.revertReason];
    return <span style={{ color: "#9ca3af", fontSize: 12 }}>{key ? t("lifecycleEvents." + key) : "—"}</span>;
  };

  const filterPills = [
    applied.search && { key: "s", label: `"${applied.search}"`, clear: () => { setSearch(""); setApplied((a) => ({ ...a, search: "" })); } },
    applied.dbId && { key: "db", label: `${t("lifecycleEvents.colDb")}: ${dbNameOf(databases.find((d) => String(dbId(d)) === String(applied.dbId)) || {}) || applied.dbId}`, clear: () => { setDbFilter(""); setApplied((a) => ({ ...a, dbId: "" })); } },
    applied.strategy && { key: "act", label: `${t("lifecycleEvents.colAction")}: ${strategyLabel(applied.strategy)}`, clear: () => { setStrategy(""); setApplied((a) => ({ ...a, strategy: "" })); } },
    applied.status && { key: "st", label: `${t("lifecycleEvents.colStatus")}: ${t("lifecycleEvents.status" + applied.status, { defaultValue: applied.status })}`, clear: () => { setStatus(""); setApplied((a) => ({ ...a, status: "" })); } },
    applied.fromUtc && { key: "f", label: `${t("lifecycleEvents.from")}: ${applied.fromUtc}`, clear: () => { setFromDate(""); setActiveRange(""); setApplied((a) => ({ ...a, fromUtc: "" })); } },
    applied.toUtc && { key: "tt", label: `${t("lifecycleEvents.to")}: ${applied.toUtc}`, clear: () => { setToDate(""); setApplied((a) => ({ ...a, toUtc: "" })); } },
  ].filter(Boolean);

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container" style={{ paddingBottom: 40 }}>
        {/* Navigation is handled by the global breadcrumb (Home / Admin / Lifecycle Events). */}
        <Hero icon="♻️" title={t("lifecycleEvents.title")} subtitle={t("lifecycleEvents.subtitle")}
          statValue={loading ? "…" : total.toLocaleString()} statLabel={t("lifecycleEvents.statTotal")}
          onRefresh={refresh} refreshing={loading} art={<RecycleArt />} />

        {info ? <div className="asf-alert" style={{ color: "#15803d", background: "#dcfce7", borderColor: "#bbf7d0" }}>✓ {info}</div> : null}

        {/* Filters */}
        <div className="asf-card">
          <div className="asf-grid">
            <div className="asf-field" style={{ flex: "0 0 auto" }}>
              <label className="asf-lbl">{t("lifecycleEvents.rangeLabel")}</label>
              <SegControl value={activeRange} options={RANGES.map((r) => ({ value: r.label, label: r.label }))}
                onChange={(v) => { const r = RANGES.find((x) => x.label === v); if (r) applyRange(r); }} />
            </div>
            <div className="asf-field" style={{ flex: "2 1 200px" }}>
              <label className="asf-lbl">{t("common.search")}</label>
              <input className="asf-input" type="text" placeholder={t("lifecycleEvents.searchPlaceholder")} value={search}
                     onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilters()} />
            </div>
            <div className="asf-field" style={{ flex: "1 1 170px" }}>
              <label className="asf-lbl">{t("lifecycleEvents.colDb")}</label>
              <Dropdown icon="🗄" value={dbFilter} onChange={setDbFilter} placeholder={t("lifecycleEvents.allDbs")}
                options={[{ value: "", label: t("lifecycleEvents.allDbs") }, ...databases.map((d) => ({ value: String(dbId(d)), label: dbNameOf(d) }))]} />
            </div>
            <div className="asf-field" style={{ flex: "1 1 150px" }}>
              <label className="asf-lbl">{t("lifecycleEvents.colAction")}</label>
              <Dropdown value={strategy} onChange={setStrategy} placeholder={t("lifecycleEvents.allStrategies")}
                options={[{ value: "", label: t("lifecycleEvents.allStrategies") }, ...STRATEGIES.map((s) => ({ value: s, label: strategyLabel(s) }))]} />
            </div>
            <div className="asf-field" style={{ flex: "1 1 140px" }}>
              <label className="asf-lbl">{t("lifecycleEvents.colStatus")}</label>
              <Dropdown value={status} onChange={setStatus} placeholder={t("lifecycleEvents.allStatuses")}
                options={[{ value: "", label: t("lifecycleEvents.allStatuses") }, ...STATUSES.map((s) => ({ value: s, label: t("lifecycleEvents.status" + s) }))]} />
            </div>
            <div className="asf-field" style={{ flex: "0 0 auto" }}>
              <label className="asf-lbl">{t("lifecycleEvents.from")}</label>
              <input className="asf-input" type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setActiveRange(""); }} />
            </div>
            <div className="asf-field" style={{ flex: "0 0 auto" }}>
              <label className="asf-lbl">{t("lifecycleEvents.to")}</label>
              <input className="asf-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button className="asf-btn" onClick={resetFilters}>{t("lifecycleEvents.clear")}</button>
            <button className="asf-btn" onClick={handleExport} disabled={items.length === 0 || exporting || loading}>
              {exporting ? t("lifecycleEvents.exporting") : `↓ ${t("lifecycleEvents.exportCsv")}`}
            </button>
            <button className="asf-cta" onClick={applyFilters}>{t("common.search")}</button>
          </div>
        </div>

        {/* Active filter pills */}
        {filterPills.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#64748b" }}>{t("lifecycleEvents.activeFilters")}</span>
            {filterPills.map((p) => (
              <span key={p.key} className="asf-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {p.label}
                <button onClick={p.clear} style={{ border: "none", background: "transparent", color: "#5b21b6", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
              </span>
            ))}
          </div>
        )}

        {error && (<div className="asf-alert"><span style={{ fontSize: 16 }}>⚠</span> {error}</div>)}

        {/* Table */}
        <div className="asf-tablecard" style={{ overflowX: "auto" }}>
          {items.length === 0 && !loading ? (
            <EmptyState icon="🗂" title={t("lifecycleEvents.empty")} />
          ) : (
            <table className="logs-table" style={{ minWidth: 880 }}>
              <thead>
                <tr>
                  <th style={{ width: 130, paddingLeft: 16 }}>{t("lifecycleEvents.colWhen")}</th>
                  <th style={{ width: 180 }}>{t("lifecycleEvents.colUser")}</th>
                  <th style={{ width: 120 }}>{t("lifecycleEvents.colAction")}</th>
                  <th>{t("lifecycleEvents.colDetail")}</th>
                  <th style={{ width: 150 }}>{t("lifecycleEvents.colDb")}</th>
                  <th style={{ width: 130 }}>{t("lifecycleEvents.colRevert")}</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}>{[130, 180, 120, 0, 150, 130].map((w, j) => (
                        <td key={j} style={j === 0 ? { paddingLeft: 16 } : {}}><div className="skeleton-line" style={{ width: j === 3 ? "85%" : "60%", height: 10, margin: 0 }} /></td>
                      ))}</tr>
                    ))
                  : items.map((ev) => {
                      const meta = strategyMeta(ev.strategy);
                      const open = expandedId === ev.id;
                      const dest = destLabel(ev);
                      const user = ev.requestedBy;
                      return (
                        <React.Fragment key={ev.id}>
                          <tr className="asf-row" onClick={() => setExpandedId(open ? null : ev.id)} style={{ cursor: "pointer", background: open ? "#f6f2ff" : undefined }}>
                            <td style={{ paddingLeft: 16, whiteSpace: "nowrap" }}>
                              <span title={fmtAbs(ev.runAtUtc)} style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{fmtRel(ev.runAtUtc)}</span>
                              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>{fmtAbs(ev.runAtUtc).slice(0, 10)}</div>
                            </td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, background: user ? avatarColor(user) : "#cbd5e1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{user ? initials(user) : "⚙"}</div>
                                <span style={{ fontSize: 13, color: user ? "#111827" : "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }} title={user || ""}>{user || t("lifecycleEvents.scheduledBy")}</span>
                              </div>
                            </td>
                            <td>
                              <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 9999, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", background: meta.bg, color: meta.color, whiteSpace: "nowrap" }}>
                                {strategyLabel(ev.strategy)}{ev.mode === "Revert" ? " ↩" : ""}
                              </span>
                            </td>
                            <td style={{ fontSize: 13, color: "#374151", maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <span className={`sl-badge lc-status-${(ev.status || "").toLowerCase()}`} style={{ marginRight: 8 }}>{t("lifecycleEvents.status" + ev.status, { defaultValue: ev.status })}</span>
                              {t("lifecycleEvents.impact", { docs: ev.docsActioned ?? 0, bytes: formatBytes(ev.bytesReclaimed) })}{dest ? ` → ${dest}` : ""}
                            </td>
                            <td style={{ fontSize: 12, color: "#6b7280" }}>
                              {ev.dbName ? <span className="asf-chip">{ev.dbName}</span> : <span style={{ color: "#d1d5db" }}>—</span>}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>{revertCell(ev)}</td>
                          </tr>
                          {open && (
                            <tr style={{ background: "#f8faff" }}>
                              <td colSpan={6} style={{ padding: "12px 16px 14px 56px" }}>
                                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
                                  {[
                                    { label: t("lifecycleEvents.colTrigger"), value: ev.trigger === "Scheduled" ? t("lifecycle.modeScheduled") : t("lifecycle.modeManual") },
                                    { label: t("lifecycleEvents.detailRequestedBy"), value: ev.requestedBy },
                                    { label: t("lifecycleEvents.colRule"), value: ev.ruleName || `#${ev.ruleId}` },
                                    { label: t("lifecycleEvents.colImpact"), value: `${ev.docsActioned ?? 0} · ${ev.chunksMatched ?? 0} · ${formatBytes(ev.bytesReclaimed)}` },
                                    { label: t("lifecycleEvents.detailDestination"), value: dest || t("lifecycleEvents.detailNone") },
                                    { label: t("lifecycleEvents.detailCutoff"), value: ev.cutoffSummary },
                                  ].filter((f) => f.value).map((f) => (
                                    <div key={f.label}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>{f.label}</div>
                                      <div style={{ color: "#111827" }}>{f.value}</div>
                                    </div>
                                  ))}
                                </div>
                                {ev.error ? <div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px", marginTop: 10, fontSize: 12 }}>{t("lifecycleEvents.detailError")}: {ev.error}</div> : null}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{loading ? t("common.loading") : t("lifecycleEvents.total", { count: total })}</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="asf-btn" onClick={() => setPage(0)} disabled={page === 0} style={{ padding: "7px 12px", fontSize: 13 }}>«</button>
              <button className="asf-btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: "7px 14px", fontSize: 13 }}>‹ {t("lifecycleEvents.prev")}</button>
              <span className="asf-chip" style={{ display: "inline-flex", alignItems: "center", fontSize: 13, padding: "7px 12px" }}>{page + 1} / {totalPages}</span>
              <button className="asf-btn" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: "7px 14px", fontSize: 13 }}>{t("lifecycleEvents.next")} ›</button>
              <button className="asf-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} style={{ padding: "7px 12px", fontSize: 13 }}>»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function destLabel(ev) {
  return ev.moveTargetType === "Tier" ? (ev.moveTargetLocationName || `#${ev.moveTargetLocationId}`)
    : ev.moveTargetType === "Database" ? (ev.moveTargetDbName || `#${ev.moveTargetDbId}`) : "";
}
